import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Loader2 } from "lucide-react";

export default function ResetPassword() {
  const { t } = useLang();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const navigate = useNavigate();
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return toast.error(t("reset.mismatch"));
    if (password.length < 6) return toast.error(t("reset.min"));
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      toast.success(t("reset.success"));
      navigate("/login");
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-white p-8">
      <div className="w-full max-w-sm ch-fade-up">
        <div className="mb-10"><Logo /></div>
        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-neutral-400">{t("reset.eyebrow")}</p>
        <h2 className="mt-3 font-display text-3xl font-bold tracking-tight">{t("reset.title")}</h2>
        {!token ? (
          <p className="mt-4 text-sm text-red-500">{t("reset.invalid")}</p>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-5">
            <div className="space-y-2">
              <Label htmlFor="pw">{t("reset.newpw")}</Label>
              <Input id="pw" data-testid="reset-password-input" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="h-11 rounded-sm" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="pw2">{t("reset.confirm")}</Label>
              <Input id="pw2" data-testid="reset-confirm-input" type="password" required value={confirm}
                onChange={(e) => setConfirm(e.target.value)} placeholder="••••••••" className="h-11 rounded-sm" />
            </div>
            <Button type="submit" data-testid="reset-submit-button" disabled={loading}
              className="h-11 w-full rounded-sm bg-black text-white hover:bg-neutral-800">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : t("reset.submit")}
            </Button>
          </form>
        )}
        <Link to="/login" data-testid="back-to-login" className="mt-6 inline-flex items-center gap-2 text-sm text-neutral-500 hover:text-black">
          <ArrowLeft className="h-4 w-4" /> {t("common.back_login")}
        </Link>
      </div>
    </div>
  );
}
